import { useQuery } from "@apollo/client/react";
import debounce from "lodash-es/debounce";
import { useCallback, useEffect } from "react";
import { UseFormSetError } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";

import { ModuleFormContext } from "../context";
import { ModuleDetailsFormFields } from "../types";
import { SEARCH_MODULE_NAME_SUGGESTIONS } from "./gql";

const DEBOUNCE_TIMEOUT = 300;

const useAsyncValidation = (
  setError: UseFormSetError<ModuleDetailsFormFields>,
  prevName?: string
) => {
  const { setAsyncValidationLoading, createdModuleId } = useTypedContext(ModuleFormContext);
  const { onError } = useTypedContext(FlashContext);

  const { refetch: loadSearchModuleSuggestions } = useQuery(SEARCH_MODULE_NAME_SUGGESTIONS, {
    variables: { input: { fields: null, fullTextSearch: null, predicates: null } },
    skip: true,
    fetchPolicy: "no-cache",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateName = useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    debounce(async (name: string) => {
      if (prevName === name && createdModuleId) {
        return;
      }

      try {
        setAsyncValidationLoading(true);
        const response = await loadSearchModuleSuggestions({
          input: {
            fullTextSearch: name,
            predicates: [],
            fields: ["slug"],
          },
        });

        const possibleValues =
          response?.data?.searchModulesSuggestions.fields[0].possibleValuesString;

        if (
          possibleValues?.counts?.length &&
          possibleValues.values.some((item) => item.toLowerCase() === name.trim().toLowerCase())
        ) {
          return setError("name", {
            type: "custom",
            message: "Module with this name already exists",
          });
        }
      } catch (error) {
        onError(error);
      } finally {
        setAsyncValidationLoading(false);
      }
    }, DEBOUNCE_TIMEOUT),
    [loadSearchModuleSuggestions, setError, onError]
  );

  useEffect(() => () => setAsyncValidationLoading(false), [setAsyncValidationLoading]);

  return { validateName };
};

export default useAsyncValidation;
